// =============================================================================
// The search overlay.
//
// Two halves, deliberately separate:
//
//   • The live list. Typing asks the child's own REST route — the same
//     typo-tolerant matcher the results page runs — so the preview and the page
//     it leads to cannot disagree about what matches.
//   • The submit. The field sits in a real GET form rendered by the header
//     block, so Enter navigates with no JavaScript involved at all. This module
//     never handles submit, and the live list still works if the form is what
//     the visitor uses.
// =============================================================================

import { lockScroll, releaseScroll, closeOnEscape } from "./overlay.js";
import { str, url } from "./config.js";

/** Wait for typing to stop before asking the server. */
const DEBOUNCE_MS = 180;

/** Rows in the live list — the design shows six. */
const MAX_RESULTS = 6;

/** Delay before focusing the field, matching the panel's own fade-in. */
const FOCUS_DELAY_MS = 150;

/** Shortest phrase worth a request; the matcher scores nothing below it. */
const MIN_QUERY = 2;

export default function initSearch() {
	const panel = document.querySelector("[data-search-panel]");
	const input = document.querySelector("[data-search-input]");
	const results = document.querySelector("[data-search-results]");

	if (!panel || !input || !results) {
		return;
	}

	let open = false;
	let debounce = 0;

	// Responses can land out of order: two keystrokes in quick succession put
	// two requests in flight, and the slower first one arriving last would
	// paint matches for a phrase no longer in the field. Only the newest
	// request is allowed to render.
	let requestId = 0;

	function setOpen(next) {
		if (next === open) {
			return;
		}
		open = next;

		panel.classList.toggle("is-open", open);

		if (open) {
			lockScroll();
			// After the fade, not before: focusing an element that is still
			// `visibility: hidden` scrolls the page to where it will be.
			setTimeout(() => input.focus(), FOCUS_DELAY_MS);
		} else {
			releaseScroll();
		}
	}

	document.querySelectorAll("[data-open-search]").forEach((button) => {
		button.addEventListener("click", () => setOpen(true));
	});

	const closeBtn = panel.querySelector("[data-close-search]");
	if (closeBtn) {
		closeBtn.addEventListener("click", () => setOpen(false));
	}

	// The panel is its own backdrop. Comparing against the panel itself is what
	// tells a click on the dimmed surround from one that started inside the
	// field and was released over the overlay.
	panel.addEventListener("click", (event) => {
		if (event.target === panel) {
			setOpen(false);
		}
	});

	closeOnEscape(() => setOpen(false));

	input.addEventListener("input", () => {
		clearTimeout(debounce);

		const term = input.value.trim();
		if (term.length < MIN_QUERY) {
			// Bump the id as well as clearing: a request already in flight for
			// the longer phrase would otherwise paint itself into an emptied
			// list a moment later.
			requestId += 1;
			results.textContent = "";
			return;
		}

		debounce = setTimeout(() => lookup(term), DEBOUNCE_MS);
	});

	async function lookup(term) {
		const thisRequest = ++requestId;

		let rows = [];

		try {
			const endpoint = new URL(url("searchApi", "/"), window.location.origin);
			endpoint.searchParams.set("q", term);
			endpoint.searchParams.set("per_page", String(MAX_RESULTS));

			const response = await fetch(endpoint.toString());
			if (!response.ok) {
				throw new Error(`Product search responded ${response.status}`);
			}

			const data = await response.json();
			rows = Array.isArray(data?.results) ? data.results.slice(0, MAX_RESULTS) : [];
		} catch {
			// Worth saying rather than leaving the previous results up: a stale
			// list silently claims those titles match a phrase they do not.
			if (thisRequest === requestId) {
				paint(hint(str("searchFailed", "Search is not responding. Please try again.")));
			}
			return;
		}

		// A newer keystroke owns the panel now.
		if (thisRequest !== requestId) {
			return;
		}

		if (rows.length === 0) {
			const message = str("searchEmpty", 'No results for "%s". Try a platform or title.');
			paint(hint(message.replace("%s", () => term)));
			return;
		}

		paint(rows.map(resultRow));
	}

	/**
	 * Replace the list.
	 *
	 * `textContent = ""` empties it before appending, which also drops any
	 * announcement the previous contents were still queued to make — the list
	 * carries `aria-live`, so replacing nodes is what a screen reader reads.
	 */
	function paint(nodes) {
		results.textContent = "";
		nodes.forEach((node) => results.appendChild(node));
	}
}

/**
 * One row of the live list.
 *
 * Built from nodes rather than an HTML string: titles and platform labels are
 * editor-authored, so interpolating them into innerHTML would make the search
 * field a place to inject markup.
 */
function resultRow(item) {
	const link = document.createElement("a");
	link.className = "search-result-row";
	link.href = typeof item.url === "string" && item.url ? item.url : "#";

	const img = document.createElement("img");
	img.src = typeof item.image === "string" ? item.image : "";
	// Decorative — the title beside it already names the product.
	img.alt = "";
	img.loading = "lazy";
	link.appendChild(img);

	const body = document.createElement("div");

	const title = document.createElement("div");
	title.className = "search-result-title";
	title.textContent = String(item.title ?? "");
	body.appendChild(title);

	// "PS5 · S$74.90", the design's single line of context. Either half may be
	// missing on a product whose fields are not filled in.
	const meta = [item.platform, item.price].filter(Boolean).join(" · ");
	if (meta) {
		const metaEl = document.createElement("div");
		metaEl.className = "search-result-meta";
		metaEl.textContent = meta;
		body.appendChild(metaEl);
	}

	link.appendChild(body);

	return link;
}

/** The "nothing found" / "search failed" line. */
function hint(text) {
	const p = document.createElement("p");
	p.className = "search-hint";
	p.textContent = text;
	return p;
}
